import { useEffect, useState } from 'react';

// Lightweight PWA install prompt. Shows on the second visit (once
// beforeinstallprompt has fired) as a dismissible banner above the bottom nav.
// iOS Safari doesn't fire beforeinstallprompt; iOS users see a one-line
// "Add to Home Screen" hint instead, dismissible per-device.

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISS_KEY = 'babcocknav.install-dismissed';
const IOS_HINT_KEY = 'babcocknav.ios-hint-dismissed';

function isIos(): boolean {
  const ua = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  const isMacTouch = ua.includes('Mac') && 'ontouchend' in document;
  return isIOSDevice || isMacTouch;
}

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // Safari iOS
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function InstallPrompt() {
  const [event, setEvent] = useState<BIPEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Already installed — nothing to prompt.

    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      setEvent(e as BIPEvent);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // iOS never fires beforeinstallprompt. Show a text-only hint.
    if (isIos() && !localStorage.getItem(IOS_HINT_KEY)) {
      // Delay so it doesn't compete with first paint.
      const t = window.setTimeout(() => setShowIosHint(true), 2000);
      return () => {
        window.removeEventListener('beforeinstallprompt', onBIP);
        window.clearTimeout(t);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, []);

  if (event) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Install Babcock Nav">
        <div className="install-prompt__text">
          <strong>Install Babcock Nav</strong>
          <span>Add to your home screen for one-tap access.</span>
        </div>
        <div className="install-prompt__actions">
          <button
            className="install-prompt__dismiss"
            onClick={() => {
              localStorage.setItem(DISMISS_KEY, '1');
              setEvent(null);
            }}
          >
            Not now
          </button>
          <button
            className="install-prompt__install"
            onClick={async () => {
              await event.prompt();
              const { outcome } = await event.userChoice;
              if (outcome === 'dismissed') {
                localStorage.setItem(DISMISS_KEY, '1');
              }
              setEvent(null);
            }}
          >
            Install
          </button>
        </div>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Add to Home Screen">
        <div className="install-prompt__text">
          <strong>Add to Home Screen</strong>
          <span>Tap Share, then "Add to Home Screen" for a full-screen app.</span>
        </div>
        <div className="install-prompt__actions">
          <button
            className="install-prompt__dismiss"
            onClick={() => {
              localStorage.setItem(IOS_HINT_KEY, '1');
              setShowIosHint(false);
            }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return null;
}
